import { useSession, signOut } from 'next-auth/react';
import { useCallback, useEffect, useRef, useMemo } from 'react';
import { getEmbeddedSession, isSessionValid, clearEmbeddedSession } from './embeddedSessionUtils';
import { refreshTenantContext } from './useTenantContext';
import { getBrowserTimezone } from './timezones';

// Module-level guard: collapse concurrent session-refresh calls into a single
// in-flight promise so multiple simultaneous 401 responses don't each kick off
// their own refresh round-trip to the token endpoint.
let pendingRefresh: Promise<unknown> | null = null;

function deduplicatedRefresh(updateFn: () => Promise<unknown>): Promise<unknown> {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = updateFn().finally(() => { pendingRefresh = null; });
  return pendingRefresh;
}

// FIX #3: Retry helper with exponential backoff
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  baseDelay = 1000
): Promise<T> {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === maxRetries - 1) throw error;
      
      const delay = baseDelay * Math.pow(2, i);
      console.log(`[Retry] Attempt ${i + 1}/${maxRetries} failed, retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw new Error('Max retries exceeded');
}

/**
 * Custom hook that provides an authenticated API fetch function
 * Automatically includes the current access token from the session
 * Handles token refresh transparently - always uses the latest token
 * Automatically prepends /api/v1 to endpoints (can be overridden by using /api/ prefix)
 * 
 * IMPORTANT: The authenticatedFetch function reference is stable across renders to prevent
 * unnecessary re-renders in components. It uses a ref to access the latest token dynamically.
 * 
 * @returns Object with authenticatedFetch function and session status
 * 
 * @example
 * const { authenticatedFetch, isAuthenticated } = useAuthenticatedApi();
 * 
 * // Automatically becomes /api/v1/agents
 * const data = await authenticatedFetch('agents', {
 *   method: 'GET'
 * });
 * 
 * // Or use full path to override
 * const data = await authenticatedFetch('/api/v2/agents', {
 *   method: 'GET'
 * });
 */
export function useAuthenticatedApi() {
  const { data: session, status, update } = useSession();
  
  // Use a ref to store the latest session data
  // This allows authenticatedFetch to always access the current token
  // without changing its function reference (preventing unnecessary re-renders)
  const sessionRef = useRef(session);
  const updateRef = useRef(update);
  const statusRef = useRef(status);
  
  // Keep refs in sync with latest values
  useEffect(() => {
    sessionRef.current = session;
    updateRef.current = update;
    statusRef.current = status;
  }, [session, update, status]);
  
  // Monitor for session errors and handle them
  useEffect(() => {
    if (session?.error === 'RefreshAccessTokenError') {
      // Check if we're on salesforce page - if so, trigger Salesforce SSO instead of generic login
      const isSalesforcePage = typeof window !== 'undefined' && 
        (window.location.pathname.startsWith('/salesforce') || 
         sessionStorage.getItem('salesforce-sso-page') === 'true');
      
      if (isSalesforcePage) {
        // Loop guard: a genuinely invalid refresh token reloads once, re-auths, and should not
        // hit this branch again soon. If it does within the cooldown, something is forcing repeat
        // failures (e.g. backend still unhappy) - stop reloading and let the login button show
        // instead of hammering the tenant with reload-after-reload.
        const lastReloadAt = Number(sessionStorage.getItem('salesforce-session-error-reload-at') || 0);
        if (Date.now() - lastReloadAt < 20_000) {
          console.warn('[useAuthenticatedApi] Session expired again shortly after last reload - skipping forced reload to avoid a loop');
          return;
        }

        console.log('[useAuthenticatedApi] Session expired, utilizing Salesforce SSO');
        sessionStorage.setItem('salesforce-session-error-reload-at', String(Date.now()));
        // Clear the flag and let the Salesforce page handle the auth
        sessionStorage.removeItem('salesforce-sso-page');
        // Clear the invalid NextAuth cookie session BEFORE reloading. Without this, the cookie
        // (still carrying the same dead refresh token) survives the reload untouched, so the
        // fresh page load hits the exact same 401 again immediately - and once the loop guard
        // above starts skipping further reloads, the user is left permanently stuck on the
        // "session expired" screen with nothing left to recover it.
        signOut({ redirect: false }).finally(() => {
          // Force reload to trigger Salesforce auth flow
          window.location.href = window.location.pathname;
        });
        return;
      }
      
      // Session refresh failed - redirect to login
      const currentPath = window.location.pathname + window.location.search;
      const redirectUrl = `/login?redirect=${encodeURIComponent(currentPath)}&error=session_expired`;
      
      console.log('[useAuthenticatedApi] Session expired, redirecting to:', redirectUrl);
      console.log('[useAuthenticatedApi] Current location:', {
        href: window.location.href,
        origin: window.location.origin,
        pathname: window.location.pathname
      });
      
      // Use window.location.href for immediate redirect (doesn't wait for NextAuth)
      window.location.href = redirectUrl;
    }
  }, [session?.error]);
  
  /**
   * Authenticated fetch wrapper that automatically includes bearer token
   * Uses the latest access token from session ref (dynamically looked up on each call)
   * Function reference is stable across renders to prevent unnecessary component re-renders
   * Automatically prepends /api/v1 to endpoints unless already prefixed with /api/
   * On 401 errors, automatically refreshes the session and retries the request once
   * If no token is available but session is loading, waits and retries
   * 
   * @param throwOnEmbeddedSessionExpiry - If true, throws error on embedded session 401 instead of retrying with OAuth.
   *                                       Use this in AIChatComponent to preserve session expiry UI handling.
   */
  const authenticatedFetch = useCallback(async <T = unknown>(
    endpoint: string,
    options: RequestInit = {},
    _isRetry = false,
    throwOnEmbeddedSessionExpiry = false,
    _isTransientEmbeddedRetry = false
  ): Promise<T> => {
    // Build headers based on auth context
    const headers: Record<string, string> = {
      // Raw browser timezone (not the profile-overridden preference) - the backend already
      // prioritizes its own stored user preference and only falls back to this for users
      // who haven't set one, per backend's guidance.
      'X-Timezone': getBrowserTimezone(),
      ...Object.fromEntries(
        Object.entries(options.headers || {}).filter(([, v]) => v != null)
      ),
    };
    
    // Opt-out signal for non-critical/background calls (e.g. trackPromptUsage) - a stray 401 on
    // one of these must NOT wipe the shared embedded session out from under every other concurrent
    // request (this is exactly what caused embedded-session-vanishes-during-prompt-flows: a
    // fire-and-forget analytics call racing the real send, getting its own 401, and clearing the
    // session for everyone). Never actually sent to the backend - stripped below.
    const skipEmbeddedSessionClearOn401 = headers['X-Skip-Session-Clear-On-401'] === 'true';
    delete headers['X-Skip-Session-Clear-On-401'];
    
    let hasAuth = false;
    
    // Get session data upfront for potential refresh logic later
    const currentSession = sessionRef.current;
    const currentToken = currentSession?.accessToken;
    const currentStatus = statusRef.current;
    
    // Check for embedded session FIRST (Salesforce embedded mode priority)
    // But validate it's not expired before using it
    if (typeof window !== 'undefined') {
      const embeddedSession = getEmbeddedSession();
      if (embeddedSession) {
        // Validate the embedded session before using it
        if (isSessionValid(embeddedSession)) {
          const actualSessionId = embeddedSession.sessionId;
          if (actualSessionId) {
            // Send session ID in custom header (backend validates via EmbeddedSessionGuard)
            headers['X-Embedded-Session'] = actualSessionId;
            hasAuth = true;
          }
        } else {
          // Session is expired, clear it so we fall back to OAuth
          clearEmbeddedSession('useAuthenticatedApi', 'Expired embedded session found during API call');
        }
      }
    }
    
    // Fall back to NextAuth session if no valid embedded session
    if (!hasAuth) {
      if (currentToken) {
        headers['Authorization'] = `Bearer ${currentToken}`;
        hasAuth = true;
      }
    }
    
    // If still no auth, try to refresh NextAuth session
    if (!hasAuth && !_isRetry) {
      // If session is still loading, wait and retry
      if (currentStatus === 'loading') {
        console.log('[useAuthenticatedApi] Session still loading, waiting for authentication...');
        await new Promise(resolve => setTimeout(resolve, 500));
        return authenticatedFetch<T>(endpoint, options, true);
      }
      
      // If session is unauthenticated, try to update/refresh it once
      if (currentStatus === 'unauthenticated') {
        console.log('[useAuthenticatedApi] Session unauthenticated, attempting refresh...');
        const updateFn = updateRef.current;
        if (updateFn) {
          await updateFn();
          await new Promise(resolve => setTimeout(resolve, 500));
          return authenticatedFetch<T>(endpoint, options, true, throwOnEmbeddedSessionExpiry);
        }
      }
    }
    
    if (!hasAuth) {
      console.error('[API] Not authenticated - no session ID or token found');
      throw new Error('Not authenticated - no access token or session ID available');
    }
    
    // Auto-prepend /api/v1 if not already prefixed with /api/. Absolute URLs (used to bypass
    // proxy.ts's /api/* rewrite for large uploads) pass through unchanged.
    const isAbsoluteUrl = /^https?:\/\//.test(endpoint);
    const fullEndpoint = isAbsoluteUrl || endpoint.startsWith('/api/')
      ? endpoint
      : `/api/v1/${endpoint.startsWith('/') ? endpoint.slice(1) : endpoint}`;
    
    // Only set Content-Type for non-FormData requests when there's a body
    if (!(options.body instanceof FormData) && options.body) {
      headers['Content-Type'] = 'application/json';
    }
    
    // Retry logic with exponential backoff for transient errors
    const maxRetries = 3;
    const baseDelay = 300; // Start with 300ms
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(fullEndpoint, {
          ...options,
          headers,
        });
        
        if (!response.ok) {
          // Handle 401 Unauthorized - session/token might be expired
          if (response.status === 401 && !_isRetry) {
            // Check if using embedded session
            if (typeof window !== 'undefined' && headers['X-Embedded-Session']) {
              if (skipEmbeddedSessionClearOn401) {
                // Non-critical background call - fail quietly, don't touch the shared session or retry
                console.warn('[API] Non-critical request got 401 - leaving embedded session untouched');
                throw new Error('Non-critical request failed authentication (401)');
              }
              
              // A 401 while our own locally-recorded expiresAt says this session should still be
              // good is more likely a transient backend blip (rolling restart, momentary
              // validation hiccup) than a genuinely revoked session - clearing immediately wipes
              // the ONE SHARED embedded session out from under every other concurrent Salesforce
              // surface (ai-chat + prompt-buttons) over something that may resolve itself a moment
              // later. Mirrors the "defer while still locally valid" pattern already used for
              // NextAuth refresh failures. Retry once (bounded via _isTransientEmbeddedRetry -
              // never recurses more than once) before clearing.
              if (!_isTransientEmbeddedRetry && isSessionValid(getEmbeddedSession())) {
                console.warn('[API] Embedded session got 401 but still looks locally valid - retrying once before clearing shared session');
                await new Promise(resolve => setTimeout(resolve, 400));
                return authenticatedFetch<T>(endpoint, options, _isRetry, throwOnEmbeddedSessionExpiry, true);
              }
              
              // Clear the invalid embedded session
              clearEmbeddedSession('useAuthenticatedApi', 'Backend returned 401 for embedded session');
              
              if (throwOnEmbeddedSessionExpiry) {
                // AIChatComponent wants to handle this error for UI state management
                console.error('[API] Embedded session expired');
                throw new Error('Session expired. Please re-authenticate.');
              } else {
                // Admin pages: silently retry with OAuth
                console.warn('[API] Embedded session invalid, retrying with OAuth token...');
                return authenticatedFetch<T>(endpoint, options, true, throwOnEmbeddedSessionExpiry);
              }
            }
            
            // Try to update the NextAuth session to get a fresh token (deduplicated across callers)
            const updateFn = updateRef.current;
            if (updateFn) {
              await deduplicatedRefresh(updateFn);
              // Wait a moment for the session to update
              await new Promise(resolve => setTimeout(resolve, 100));
              // Retry the request with the fresh token (only once to prevent infinite loops)
              return authenticatedFetch<T>(endpoint, options, true, throwOnEmbeddedSessionExpiry);
            }
            throw new Error('Authentication failed - please try again or refresh the page');
          }
          
          // Handle 403 — a real-time signal that's faster than the tenant-context poll, so any
          // suspension 403 forces an immediate refetch instead of waiting up to 30s for it.
          if (response.status === 403) {
            const errorText = await response.text();
            let body: Record<string, unknown> | null = null;
            try { body = JSON.parse(errorText); } catch { /* not JSON */ }
            if (body?.suspended === true) {
              void refreshTenantContext();
            }
            throw new Error(`API Error: 403 Forbidden${errorText ? ` - ${errorText}` : ''}`);
          }

          // For server errors (500-504), retry with backoff
          if (response.status >= 500 && response.status <= 504 && attempt < maxRetries - 1) {
            const delay = baseDelay * Math.pow(2, attempt);
            console.warn(`[authenticatedFetch] Server error ${response.status}, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue; // Retry
          }
          
          const errorText = await response.text();
          throw new Error(
            `API Error: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`
          );
        }
        
        const responseText = await response.text();
        
        if (!responseText) {
          return undefined as T;
        }
        
        return JSON.parse(responseText) as T;
        
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        // Don't retry on parse errors or explicit API errors (already thrown above)
        if (lastError.message.includes('API Error') || lastError.message.includes('JSON') || lastError.message === 'TENANT_SUSPENDED') {
          throw lastError;
        }
        
        // Network errors (connection refused, timeout, etc.) - retry with backoff
        if (attempt < maxRetries - 1) {
          const delay = baseDelay * Math.pow(2, attempt);
          console.warn(`[authenticatedFetch] Network error, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries}):`, lastError.message);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue; // Retry
        }
        
        // Last attempt failed, throw the error
        throw lastError;
      }
    }
    
    // Should never reach here, but TypeScript needs it
    throw lastError || new Error('Request failed after retries');
  }, []); // Empty deps - function reference is stable, uses refs for dynamic values
  
  // Memoize isAuthenticated to prevent unnecessary re-renders
  const isAuthenticated = useMemo(
    () => status === 'authenticated' && !!(session?.accessToken) && !session?.error,
    [status, session?.accessToken, session?.error]
  );
  
  // FIX #3: Wrap updateSession with retry logic
  const updateSession = useCallback(
    () => retryWithBackoff(
      () => deduplicatedRefresh(updateRef.current),
      3,  // 3 retries
      1000 // Start with 1s delay
    ),
    []
  );

  return {
    authenticatedFetch,
    isAuthenticated,
    isLoading: status === 'loading',
    session,
    updateSession,
  };
}
